import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck, Ticket, Music, Info } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow, parseISO } from "date-fns";

// Icon map so different notification kinds have distinct visual weight.
const ICONS = {
    gig_reminder: Music,
    ticket_refunded: Ticket,
    default: Info,
};

/**
 * NotificationBell — polls /api/notifications every 60s while signed in.
 * Click the bell → dropdown lists recent notifications; click one → marks read + navigates to url.
 * "Mark all read" button clears the badge.
 */
export default function NotificationBell() {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const fetchAll = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data.notifications || []);
            setUnread(data.unread || 0);
        } catch { /* ignore — 401 on guest is fine, bell is only rendered for signed-in users */ }
    };

    useEffect(() => {
        fetchAll();
        timerRef.current = setInterval(fetchAll, 60000);
        return () => clearInterval(timerRef.current);
    }, []);

    const openItem = async (n) => {
        setOpen(false);
        if (!n.read) {
            try { await api.post(`/notifications/${n.notification_id}/read`); } catch {}
        }
        setItems((prev) => prev.map((x) => x.notification_id === n.notification_id ? { ...x, read: true } : x));
        setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
        if (n.url) navigate(n.url);
    };

    const markAll = async () => {
        try {
            await api.post("/notifications/mark-all-read");
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnread(0);
        } catch {}
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild data-testid="notification-bell-trigger">
                <button className="relative w-9 h-9 rounded-full border border-stone-300 bg-white flex items-center justify-center hover:bg-secondary transition-colors" title="Notifications">
                    <Bell size={16} />
                    {unread > 0 && (
                        <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center border-2 border-white">
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Notifications</span>
                    {unread > 0 && (
                        <button
                            data-testid="notification-mark-all-read"
                            onClick={markAll}
                            className="text-xs font-bold text-primary hover:underline inline-flex items-center gap-1"
                        >
                            <CheckCheck size={12}/> Mark all read
                        </button>
                    )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {items.length === 0 && (
                    <div data-testid="notifications-empty" className="px-3 py-4 text-sm text-muted-foreground text-center">
                        You're all caught up.
                    </div>
                )}
                <div className="max-h-80 overflow-y-auto">
                    {items.map((n) => {
                        const Icon = ICONS[n.kind] || ICONS.default;
                        return (
                            <button
                                key={n.notification_id}
                                data-testid={`notification-${n.notification_id}`}
                                onClick={() => openItem(n)}
                                className={`w-full text-left px-3 py-2.5 border-b border-stone-50 last:border-0 flex gap-2 items-start hover:bg-secondary transition-colors ${
                                    !n.read ? "bg-accent/5" : ""
                                }`}
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.read ? "bg-stone-100 text-muted-foreground" : "bg-accent/15 text-accent"}`}>
                                    <Icon size={14}/>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className={`text-sm truncate ${!n.read ? "font-bold" : "font-medium"}`}>{n.title}</div>
                                    {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">
                                        {n.created_at ? formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }) : ""}
                                    </div>
                                </div>
                                {!n.read && <span className="w-2 h-2 rounded-full bg-accent shrink-0 mt-1.5"/>}
                            </button>
                        );
                    })}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
